import React, { useState, useRef } from 'react';
import { IconButton, Popover, Tooltip, Typography } from '@material-ui/core';
import { classes } from 'typestyle';
import { ReduxSlice as FilterSlice } from 'src/components/FilterPanel/FilterPanel.container';
import type { ContextFilterConfig } from 'configuration-schemas/confdefn';
import { IconLink } from 'src/components/Headerbar/Links/IconLink';
import styles, { buildFilterIconClass } from 'src/components/Headerbar/Headerbar.styles';
import { mediumBorderColor } from 'src/utils/Style/Theme';
import { flatMap, sortBy } from 'lodash';
import CloseIcon from '@material-ui/icons/Close';
import { SelectionOverride } from 'src/common-ui/components/Filters/Filters';

interface FiltersLinkProps {
  filtersEnabled: boolean;
  filterSlice: FilterSlice;
  onFiltersOpen: () => void;
  contextFilterConfig?: ContextFilterConfig;
  /** These control the removing of filter selections (individual or whole sections) */
  onRemoveFilterSelection: (override: SelectionOverride) => void;
  onSubmitFilterSelections: () => Promise<void>;
  onClearFilterSection: (sectionId: string) => void;
}

interface GroupedFilterSelection {
  filterName: string;
  selections: string[];
}

export const FiltersLink = ({
  filtersEnabled,
  filterSlice,
  onFiltersOpen,
  contextFilterConfig,
  onRemoveFilterSelection,
  onSubmitFilterSelections,
  onClearFilterSection,
}: FiltersLinkProps) => {
  const isFiltersEnabled = filterSlice.isFilterSensitive && filtersEnabled;

  const filteredSelections = filterSlice.lastSelections.filter((selection) => {
    if (!contextFilterConfig) return true;

    // Find which group this filter belongs to
    const parentGroup = filterSlice.state.find((group) =>
      group.filters.some((filter) => filter.id === selection.filterDefnId),
    );
    if (!parentGroup) return true;

    const isInBlacklist =
      contextFilterConfig?.groups?.blacklist && contextFilterConfig.groups.blacklist.includes(parentGroup.id);

    return !isInBlacklist;
  });

  /** group selections by their filter name for popover display */
  const groupedSelections = filteredSelections.reduce<GroupedFilterSelection[]>((acc, selection) => {
    const parentGroup = filterSlice.state.find((group) =>
      group.filters.some((filter) => filter.id === selection.filterDefnId),
    );
    const matchedFilter = parentGroup?.filters.find((filter) => filter.id === selection.filterDefnId);
    const filterName = matchedFilter?.name ?? selection.filterDefnId;

    const existing = acc.find((item) => item.filterName === filterName);
    if (existing) {
      existing.selections.push(selection.id);
    } else {
      acc.push({ filterName, selections: [selection.id] });
    }

    return acc;
  }, []);

  /** sort grouped selections to match the order filters appear in the filter panel */
  const filterPanelOrder = flatMap(filterSlice.state, (group) => group.filters).reduce<Record<string, number>>(
    (acc, filter, index) => ({ ...acc, [filter.name]: index }),
    {},
  );

  const sortedGroupedSelections = sortBy(groupedSelections, (g) => filterPanelOrder[g.filterName] ?? Infinity);

  const hasFilterSelections = Boolean(
    filterSlice.isFilterSensitive && filteredSelections.length > 0 && isFiltersEnabled,
  );
  const [isOpen, setPopoverOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const anchorElement = useRef(null);

  const onLinkClick = () => {
    if (isFiltersEnabled) {
      onFiltersOpen();
    }
    setPopoverOpen(true);
  };

  const handleMouseOver = () => setPopoverOpen(true);
  const handleMouseLeave = () => setPopoverOpen(false);

  return (
    <React.Fragment>
      <Tooltip title={isFiltersEnabled ? '' : 'This tab is not filter sensitive'}>
        <IconLink
          id="FiltersLink"
          iconClass={buildFilterIconClass(hasFilterSelections)}
          linkClass={classes(`${isFiltersEnabled ? '' : 'disabled'}`)}
          text="Filters"
          onClick={onLinkClick}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          ref={anchorElement}
          data-qa="headerbar-filter-icon"
        />
      </Tooltip>
      {isFiltersEnabled && (
        <Popover
          open={isOpen}
          anchorEl={anchorElement.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          classes={{ root: styles.filterPopover }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            onMouseEnter: handleMouseOver,
            onMouseLeave: handleMouseLeave,
            style: { maxHeight: '80vh' },
          }}
          disableRestoreFocus={true}
        >
          <div style={{ minWidth: 200, display: 'flex', flexDirection: 'column', maxHeight: 'calc(80vh - 2px)' }}>
            <Typography style={{ textAlign: 'center' }}>Selected Filters ({filteredSelections.length})</Typography>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px 8px 8px' }}>
              {sortedGroupedSelections.map(({ filterName, selections }, index) => (
                <div key={filterName}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onMouseEnter={() => setHoveredSection(filterName)}
                    onMouseLeave={() => setHoveredSection(null)}
                  >
                    <Typography
                      variant="overline"
                      style={{
                        color: hoveredSection === filterName ? '#003d36' : '#005c54',
                        transition: 'color 0.15s',
                      }}
                    >
                      {filterName}
                    </Typography>
                    <Tooltip title="Remove all selections for this filter" placement="right">
                      <IconButton
                        size="small"
                        style={{
                          visibility: hoveredSection === filterName ? 'visible' : 'hidden',
                          padding: 0,
                          color: '#005c54',
                          marginLeft: 4,
                          marginBottom: 3,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const matchedFilter = filterSlice.state
                            .flatMap((group) => group.filters)
                            .find((f) => f.name === filterName);

                          if (matchedFilter) {
                            onClearFilterSection(matchedFilter.id);
                            onSubmitFilterSelections();
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <ul>
                    {selections.map((id) => (
                      <li
                        key={id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingRight: 8,
                        }}
                        onMouseEnter={() => setHoveredId(`${filterName}-${id}`)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <div
                          className="list-popover-container"
                          style={{
                            color: hoveredId === `${filterName}-${id}` ? '#005c54' : 'inherit',
                            transition: 'color 0.15s',
                            cursor: 'default',
                          }}
                        >
                          <div>{id}</div>
                        </div>
                        <Tooltip title="Remove this filter" placement="right">
                          <IconButton
                            size="small"
                            style={{
                              visibility: hoveredId === `${filterName}-${id}` ? 'visible' : 'hidden',
                              padding: 0,
                              color: '#005c54',
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // prevent popover close from click bubbling
                              const matchedFilter = filterSlice.state
                                .flatMap((group) => group.filters)
                                .find((f) => f.name === filterName);

                              if (matchedFilter) {
                                onRemoveFilterSelection({
                                  id,
                                  filterDefnId: matchedFilter.id,
                                  value: false,
                                });
                                onSubmitFilterSelections();
                              }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </li>
                    ))}
                  </ul>
                  {index < groupedSelections.length - 1 && (
                    <hr style={{ margin: '0', borderTop: `1px solid ${mediumBorderColor}` }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </Popover>
      )}
    </React.Fragment>
  );
};
